import React from 'react';
import Promise from 'bluebird';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import usePprStats, { TARGET_FIELDS, KPI_STATUS_TYPE } from '../../../hooks/usePprStats';
import { MemberDetailsContext } from './MemberDetails';
import { withStyles } from '@material-ui/core/styles';
import Button from '../../../components/Button';
import GetPeriodPlanResultQuery from '../../../common/graphql/GetPeriodPlanResult';
import { CREATE_PERIOD_PLAN_RESULT } from '../../../common/graphql/CreatePeriodPlanResult';
import { useQuery, useMutation } from 'react-apollo-hooks';
import get from 'lodash/get';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import upperCase from 'lodash/upperCase';
import groupBy from 'lodash/groupBy';
import flatten from 'lodash/flatten';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Map } from 'immutable';
import MyTeamContext, { isMemberActive } from '../../../contexts/MyTeamContext';
import Divider from '@material-ui/core/Divider';
import PeriodLabel from '../../../components/PeriodLabel';
import set from 'lodash/fp/set';
import Message from '../../../components/Message';

function InputRow({
  id,
  classes,
  label,
  focused,
  defaultValue,
  adornment,
  onChange,
  color,
  type,
  group,
}) {
  // focusField === id && console.log(id, focusField === id); //TRACE
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (inputRef && inputRef.current) {
      focused && inputRef.current.focus();
    }
  }, []);
  function handleOnChange(...args) {
    onChange && onChange(...args);
  }
  const textProps = { defaultValue };
  // if (defaultValue) {
  //   textProps.defaultValue = defaultValue;
  // }
  const inputProps = React.useMemo(() => {
    return {
      startAdornment: <InputAdornment position="start">{adornment || ''}</InputAdornment>,
      style: { color, width: 150 },
    };
  }, [adornment, color]);
  return (
    <>
      <div className={classes[`${type}Input`] + ' ' + classes.row}>
        {type === 'actual' && (
          <span className={classes.lbl}>
            <Typography variant="subtitle1" gutterBottom>
              {group}
            </Typography>
          </span>
        )}
        <div>
          <TextField
            {...textProps}
            id={id}
            inputRef={inputRef}
            variant="outlined"
            // className={classNames(classes.margin, classes.textField)}
            InputProps={inputProps}
            onChange={handleOnChange}
            className={classes.textField}
          />
        </div>
      </div>
      {type === 'target' && <div style={{ clear: 'both' }} />}
    </>
  );
}

function Actions({ className, disabled }) {
  const [state, setState, handlers] = React.useContext(MemberDetailsContext);
  const { state: myTeamContextState, refetchMemberPPr } = React.useContext(MyTeamContext);

  const memberId = state.getIn(['member', 'id']);

  const createPeriodPlanResult = useMutation(CREATE_PERIOD_PLAN_RESULT.mutation, {
    refetchQueries: () => {
      Promise.delay(200).then(() => refetchMemberPPr(memberId));
      // return [get(myTeamContextState, 'context.dialog.refQueryKey')];
      return [];
    },
  });

  async function save() {
    setState(oldState => oldState.set('loading', true));
    const periodPlanResultPeriodId = get(myTeamContextState, 'context.periodId'); //"cb642a5d-2dbf-4593-b73e-0c68e0afa388"
    const periodPlanResultMemberId = state.getIn(['member', 'id']);
    const updateData = state.get('updateData');

    if (!updateData || !periodPlanResultPeriodId || !periodPlanResultMemberId) return;
    const updatedFields = updateData.toJS();

    const refData = state.get('refData', {});
    const targetFields = Object.keys(TARGET_FIELDS).map(
      tFkeys => TARGET_FIELDS[tFkeys].key,
    );
    const refFields = pick(refData, targetFields) || {};

    const fields = merge(refFields, updatedFields);
    const cleanedFields = {};
    Object.entries(fields).forEach(([fKey, fVal]) => {
      let newVal = fVal;
      if (fVal === '') {
        newVal = null;
      }
      cleanedFields[fKey] = newVal;
    });
    const ret = await createPeriodPlanResult({
      variables: {
        input: {
          ...cleanedFields,
          name: `${periodPlanResultPeriodId}:${periodPlanResultMemberId}`,
          periodPlanResultPeriodId,
          periodPlanResultMemberId,
        },
      },
    });
    setState(oldState => oldState.set('loading', false).set('updateData', Map({})));
    handlers.handleClose();
  }
  const hasUpdatedKeys = React.useMemo(
    () => Object.keys(state.get('updateData', Map({})).toJS()).length === 0,
    [state],
  );
  return (
    <div className={className}>
      <Button onClick={handlers.handleClose}>Cancel</Button>
      <Button
        disabled={disabled || hasUpdatedKeys || state.get('loading')}
        onClick={save}
        color="primary"
        style={{ marginLeft: 5 }}
        autoFocus
      >
        Save
      </Button>
    </div>
  );
}

const ColumnHeader = withStyles(theme => ({
  root: { ...theme.typography, marginLeft: '33%', marginRight: 15 },
}))(function(props) {
  return <span className={props.classes.root}>{upperCase(props.text)}</span>;
});

const TargetsForm = withStyles({
  root: { minHeight: 390, overflowX: 'hidden' },
  actualInput: {
    position: 'relative',
    marginTop: 5,
    float: 'left',
    width: 350,
    '& div': {
      float: 'right',
    },
  },
  targetInput: {
    marginTop: 5,
    float: 'right',
    position: 'relative',
    marginLeft: 10,
    width: 150,
    display: 'inline-block',
  },
  row: {
    // width: '100%', height: 65, padding: '10px 0'
  },
  textField: { width: 200 },
  lbl: { display: 'inline-block', width: 100, position: 'relative', top: 15 },
  actions: { height: 60, textAlign: 'right', marginTop: 30 },
  innerDivider: { marginTop: 10 },
})(props => {
  const { classes, planResultId } = props;
  const [state, setState] = React.useContext(MemberDetailsContext);
  const [isActive, setIsActive] = React.useState(true);
  const focusField = state.get('focusField');

  const { data, loading } = useQuery(GetPeriodPlanResultQuery().query, {
    skip: !planResultId,
    variables: { periodPlanResultId: planResultId },
    fetchPolicy: 'network-only',
  });
  const handlers = {
    inputChange(evt) {
      const { id, value } = evt.target;
      setState(oldState =>
        oldState
          .set('refData', get(data, 'getPeriodPlanResult'))
          .update('updateData', Map({}), f =>
            f.merge({
              [id]: value,
            }),
          ),
      );
    },
  };
  const snapShot = React.useMemo(() => {
    const { updateData } = state.toJS();
    return { ...get(data, 'getPeriodPlanResult', {}), ...updateData };
  }, [state, data]);

  React.useEffect(() => {
    const member = state.get('member');
    const isActive = isMemberActive(set('planResults.items.0', snapShot)(member));
    setIsActive(isActive);
  }, [snapShot, state.get('member')]);

  const { kpiByKey } = usePprStats(snapShot);

  function createRow(id, label, defaultValue = '', adornment, type, group) {
    return (
      <InputRow
        id={id}
        key={id}
        classes={classes}
        label={label}
        defaultValue={defaultValue}
        focused={focusField === id}
        onChange={handlers.inputChange}
        adornment={adornment}
        color={get(KPI_STATUS_TYPE, [kpiByKey[id], 'color'])}
        type={type}
        group={group}
      />
    );
  }
  const { fieldItems, fieldTypes } = React.useMemo(() => {
    // const fieldTypes = Object.keys(groupBy(TARGET_FIELDS, 'type'));
    const fieldGrpTmp = groupBy(TARGET_FIELDS, 'group');
    const fieldItems = flatten(
      Object.keys(fieldGrpTmp).map(gKey => {
        return fieldGrpTmp[gKey].sort(fld => fld.type).reverse();
      }),
    );
    console.log('>>MyTeamPage/TargetsForm::', 'fieldItems', fieldItems); //TRACE
    return { fieldItems, fieldTypes: ['actual', 'target'] };
  }, [TARGET_FIELDS]);

  const swipeableActions = state.get('swipeableActions');
  React.useLayoutEffect(() => {
    if (!swipeableActions) return;
    Promise.delay(500).then(() => {
      swipeableActions.updateHeight();
    });
  }, [isActive, loading]);

  return (
    <div className={classes.root}>
      <Typography variant="h6" component="h3">
        <PeriodLabel period={get(data, 'getPeriodPlanResult.period')} />
      </Typography>
      <Divider />
      {loading && <LinearProgress />}
      <div style={{ marginTop: 20 }}>
        <div>
          {fieldTypes.map(gKey => (
            <ColumnHeader key={gKey} text={gKey} />
          ))}
        </div>
        {!loading &&
          fieldItems.map(fld => {
            return createRow(
              fld.key,
              fld.label,
              get(data, ['getPeriodPlanResult', fld.key], ''),
              fld.adornment,
              fld.type,
              fld.group,
            );
          })}
      </div>
      {!isActive && !loading && (
        <Message
          variant="warning"
          content={`Current "Actual Wage" is empty. This member will be "Inactive".`}
          style={{ marginTop: 20 }}
        />
      )}
      <div>{!loading && <Actions className={classes.actions} disabled={loading} />}</div>
    </div>
  );
});

export default TargetsForm;
